import { isSprinkler, sprayR, sprayArc } from './spray';
import { areaCoverage, needsWater } from './coverage';

// sprinkler hydraulics (guide values, editable per head via f.flow): l/min at 360° for the default radius, min. working pressure
export const HEAD = {
  g27: { kind: 'spray', q: 12, r: 4, bar: 2 }, g29: { kind: 'spray', q: 20, r: 6, bar: 2 },
  g28: { kind: 'rotor', q: 25, r: 12, bar: 2.5 }, g03: { kind: 'rotor', q: 18, r: 8, bar: 2.5 },
  g30: { kind: 'micro', q: 1.5, r: 2, bar: 1.5 }, g31: { kind: 'drip', lph: 4, bar: 1 },
};
export const isHead = (f) => !!HEAD[f?.catId];
export const headKind = (f) => HEAD[f.catId]?.kind;
const num = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) && n > 0 ? n : null; };
export const headFlow = (f) => {
  const h = HEAD[f.catId];
  if (!h) return 0;
  if (num(f.flow)) return num(f.flow);
  if (h.kind === 'drip') return h.lph / 60;
  return h.q * (sprayR(f) / h.r) ** 2 * (sprayArc(f) / 360);
};
// precipitation rate of one head in head-to-head square spacing (mm/h)
export const headPR = (f) => (isSprinkler(f) ? (headFlow(f) * 60) / (sprayR(f) ** 2 * (sprayArc(f) / 360)) : 0);

export const PLANTS = { lawn: { mm: 25, days: 3, l: 0 }, bed: { mm: 15, days: 2, l: 2 }, hedge: { mm: 12, days: 2, l: 3 }, veg: { mm: 20, days: 4, l: 1.5 }, tree: { mm: 15, days: 1, l: 10 } };
export const SOILS = { sand: 25, loam: 13, clay: 6 };
export const irrCfg = (p) => ({ bar: num(p.irr?.bar), lpm: num(p.irr?.lpm), soil: p.irr?.soil || 'loam', start: p.irr?.start || '05:00', zones: p.irr?.zones || [] });

// per zone: flow vs. supply, mixed head types, pressure, run time (cycle & soak when the soil cannot take the rain rate)
export function zonePlan(project) {
  const c = irrCfg(project), heads = (project.fittings || []).filter(isHead);
  const zones = c.zones.map((z) => ({ ...z, heads: heads.filter((f) => f.zone === z.id) }));
  const free = heads.filter((f) => !c.zones.some((z) => z.id === f.zone));
  if (free.length) zones.push({ id: '', name: null, plant: free.every((f) => headKind(f) === 'drip') ? 'bed' : 'lawn', heads: free, free: true });
  const rows = zones.map((z) => {
    const kinds = [...new Set(z.heads.map(headKind))], pl = PLANTS[z.plant] || PLANTS.lawn;
    const q = z.heads.reduce((a, f) => a + headFlow(f), 0), spr = z.heads.filter(isSprinkler);
    const pr = spr.length ? spr.reduce((a, f) => a + headPR(f), 0) / spr.length : 0;
    const per = pl.mm / pl.days, drip = !spr.length && z.heads.length > 0;
    const min = drip ? ((pl.l || 2) / HEAD.g31.lph) * 60 : pr ? (per / pr) * 60 : 0;
    const infil = SOILS[c.soil] || 13, cycles = pr > infil ? Math.min(4, Math.ceil(pr / infil), Math.max(1, Math.floor(min / 3))) : 1;
    const warn = [];
    if (kinds.includes('drip') && kinds.length > 1) warn.push('mixDrip');
    if (kinds.includes('spray') && kinds.includes('rotor')) warn.push('mixPr');
    if (c.lpm && q > 0.8 * c.lpm) warn.push(z.heads.length > 1 ? 'flow' : 'flowOne');
    const needBar = Math.max(0, ...z.heads.map((f) => HEAD[f.catId].bar));
    if (c.bar && needBar && c.bar < needBar + 0.5) warn.push('bar');
    if (cycles > 1) warn.push('soak');
    return { ...z, kinds, q, pr, per, min: Math.ceil(min), cycles, days: pl.days, warn, needBar, share: c.lpm ? q / c.lpm : null };
  });
  // one valve at a time; while a zone soaks (30 min) the next zones run -> start times per cycle
  let clock = (() => { const [h, m] = c.start.split(':').map(Number); return (h || 0) * 60 + (m || 0); })();
  const q = rows.filter((z) => z.min > 0).map((z) => ({ z, left: z.cycles, ready: 0, run: Math.ceil(z.min / z.cycles) }));
  rows.forEach((z) => { z.starts = []; });
  while (q.some((x) => x.left)) {
    const next = q.find((x) => x.left && x.ready <= clock);
    if (!next) { clock = Math.min(...q.filter((x) => x.left).map((x) => x.ready)); continue; }
    next.z.starts.push(clock); clock += next.run; next.left -= 1; next.ready = clock + 30;
  }
  rows.forEach((z) => { z.start = z.starts[0] ?? clock; z.end = z.starts.length ? z.starts[z.starts.length - 1] + Math.ceil(z.min / z.cycles) : z.start; if (z.start < 600 && z.end > 600) z.warn.push('late'); });
  return { cfg: c, rows, heads, end: clock };
}
export const hhmm = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// zones automatically: same head kind together, filled up to 80 % of the supply, in west-east order
export function autoZones(project, t) {
  const c = irrCfg(project), lim = (c.lpm || 20) * 0.8, heads = (project.fittings || []).filter(isHead).sort((a, b) => a.lon - b.lon || a.lat - b.lat);
  const zones = [], map = {};
  ['spray', 'rotor', 'micro', 'drip'].forEach((k) => {
    const mine = [];
    heads.filter((f) => headKind(f) === k).forEach((f) => {
      let z = mine.find((x) => x.q + headFlow(f) <= lim);
      if (!z) { z = { id: `z${Date.now().toString(36)}${zones.length}`, name: `${t.izZone} ${zones.length + 1}`, plant: k === 'drip' ? 'bed' : 'lawn', q: 0 }; zones.push(z); mine.push(z); }
      z.q += headFlow(f); map[f.id] = z.id;
    });
  });
  return { zones: zones.map(({ q, ...z }) => z), map };
}

// local metric frame of a garden area
const frame = (pts) => {
  const lat0 = pts[0][0], lon0 = pts[0][1], mLat = 111320, mLon = 111320 * Math.cos((lat0 * Math.PI) / 180);
  return { xy: (la, lo) => [(lo - lon0) * mLon, (la - lat0) * mLat], ll: (x, y) => [lat0 + y / mLat, lon0 + x / mLon] };
};
const inPoly = (x, y, P) => {
  let c = false;
  for (let i = 0, j = P.length - 1; i < P.length; j = i++) if ((P[i][1] > y) !== (P[j][1] > y) && x < ((P[j][0] - P[i][0]) * (y - P[i][1])) / (P[j][1] - P[i][1]) + P[i][0]) c = !c;
  return c;
};
const edgeDist = (x, y, P) => Math.min(...P.map((a, i) => {
  const b = P[(i + 1) % P.length], dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1;
  const u = Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / L2));
  return Math.hypot(x - a[0] - u * dx, y - a[1] - u * dy);
}));
// arc + direction from the directions that point into the area
const arcAt = (x, y, P) => {
  const ins = [];
  for (let a = 0; a < 360; a += 5) { const r = (a * Math.PI) / 180; if (inPoly(x + 0.25 * Math.sin(r), y + 0.25 * Math.cos(r), P)) ins.push(r); }
  if (!ins.length) return null;
  const arc = [90, 180, 270, 360].reduce((b, v) => (Math.abs(v - ins.length * 5) < Math.abs(b - ins.length * 5) ? v : b), 360);
  const dir = (Math.round(((Math.atan2(ins.reduce((s, r) => s + Math.sin(r), 0), ins.reduce((s, r) => s + Math.cos(r), 0)) * 180) / Math.PI + 360) / 5) * 5) % 360;
  return { arc, dir: arc === 360 ? 0 : dir };
};
// typical width of the area -> head type and radius (head-to-head = spacing equals radius)
export function pickHead(g) {
  const { xy } = frame(g.pts), P = g.pts.map((p) => xy(p[0], p[1]));
  let A = 0, per = 0;
  P.forEach((a, i) => { const b = P[(i + 1) % P.length]; A += a[0] * b[1] - b[0] * a[1]; per += Math.hypot(b[0] - a[0], b[1] - a[1]); });
  const w = (2 * Math.abs(A / 2)) / (per / 2 || 1);
  const [catId, rMax, rMin] = w <= 6 ? ['g27', 4.5, 2] : w <= 16 ? ['g29', 6, 3.5] : ['g28', 12, 7];
  return { catId, r: Math.round(Math.max(rMin, Math.min(rMax, w / Math.ceil(w / rMax - 0.05))) * 2) / 2 };
}
// head-to-head proposal: corners + edge points + inner grid along the longest edge, spacing = radius
export function autoHeads(g, r) {
  const { xy, ll } = frame(g.pts), P = g.pts.map((p) => xy(p[0], p[1])), out = [];
  const add = (x, y) => { if (out.some((o) => Math.hypot(o.x - x, o.y - y) < r * 0.45)) return; const a = arcAt(x, y, P); if (a) out.push({ x, y, ...a }); };
  P.forEach((a) => add(a[0], a[1]));
  P.forEach((a, i) => { const b = P[(i + 1) % P.length], L = Math.hypot(b[0] - a[0], b[1] - a[1]), n = Math.ceil(L / r); for (let k = 1; k < n; k++) add(a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n); });
  let best = [P[0], P[1]];
  P.forEach((a, i) => { const b = P[(i + 1) % P.length]; if (Math.hypot(b[0] - a[0], b[1] - a[1]) > Math.hypot(best[1][0] - best[0][0], best[1][1] - best[0][1])) best = [a, b]; });
  const th = Math.atan2(best[1][1] - best[0][1], best[1][0] - best[0][0]), cs = Math.cos(th), sn = Math.sin(th);
  const R = P.map(([x, y]) => [x * cs + y * sn, -x * sn + y * cs]), us = R.map((p) => p[0]), vs = R.map((p) => p[1]);
  for (let v = Math.min(...vs) + r; v < Math.max(...vs); v += r) {
    for (let u = Math.min(...us) + r; u < Math.max(...us); u += r) {
      const x = u * cs - v * sn, y = u * sn + v * cs;
      if (inPoly(x, y, P) && edgeDist(x, y, P) > r * 0.5) add(x, y);
    }
  }
  return out.map((o) => { const [lat, lon] = ll(o.x, o.y); return { lat, lon, arc: o.arc, dir: o.dir }; });
}

// proposal without the spots existing sprinklers already serve
export function proposal(g, fittings) {
  const ph = pickHead(g), have = (fittings || []).filter(isSprinkler);
  const near = (h) => have.some((f) => Math.hypot((f.lat - h.lat) * 111320, (f.lon - h.lon) * 111320 * Math.cos((h.lat * Math.PI) / 180)) < ph.r * 0.5);
  return { ph, heads: (r) => autoHeads(g, r).filter((h) => !near(h)), have: have.length };
}

// applied water per watering (mm) vs. need of the zone: dry / too little / fitting / too much / run-off
const share = (rho) => (1.5 - rho) / 0.8333;
export function rainGrid(g, project) {
  const { rows } = zonePlan(project), minOf = {}, need = {};
  rows.forEach((z) => z.heads.forEach((f) => { minOf[f.id] = z.min; need[f.id] = z.per; }));
  const { xy, ll } = frame(g.pts), P = g.pts.map((p) => xy(p[0], p[1]));
  const xs = P.map((p) => p[0]), ys = P.map((p) => p[1]), [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const cell = Math.max(0.5, Math.sqrt(((x1 - x0) * (y1 - y0)) / 2500));
  const spr = (project.fittings || []).filter(isSprinkler).map((f) => ({ c: xy(f.lat, f.lon), r: sprayR(f), arc: sprayArc(f), dir: +f.dir || 0, q: headFlow(f), min: minOf[f.id] || 0, need: need[f.id] || PLANTS.lawn.mm / PLANTS.lawn.days }));
  const cells = [], cnt = { dry: 0, low: 0, ok: 0, high: 0, run: 0 };
  let sum = 0, n = 0;
  for (let y = y0 + cell / 2; y < y1; y += cell) {
    for (let x = x0 + cell / 2; x < x1; x += cell) {
      if (!inPoly(x, y, P)) continue;
      let mm = 0, nd = 0;
      spr.forEach((s) => {
        const dx = x - s.c[0], dy = y - s.c[1], d = Math.hypot(dx, dy);
        if (d > s.r || (s.arc < 360 && Math.abs(((Math.atan2(dx, dy) * 180) / Math.PI - s.dir + 540) % 360 - 180) > s.arc / 2)) return;
        mm += ((s.q * 60 * share(d / s.r)) / (Math.PI * s.r * s.r * (s.arc / 360))) * (s.min / 60);
        nd = Math.max(nd, s.need);
      });
      const k = !mm ? 'dry' : mm < 0.6 * nd ? 'low' : mm <= 1.4 * nd ? 'ok' : mm <= 2 * nd ? 'high' : 'run';
      cnt[k] += 1; sum += mm; n += 1;
      if (k !== 'ok') cells.push({ k, b: [ll(x - cell / 2, y - cell / 2), ll(x + cell / 2, y + cell / 2)] });
    }
  }
  const tot = n || 1;
  return { cells, mean: sum / tot, pct: Object.fromEntries(Object.entries(cnt).map(([k, v]) => [k, Math.round((v / tot) * 100)])) };
}

// one-line-per-topic irrigation summary (Leakbuster briefing + PDF)
export function irrSummary(project, strict = !!project.dryStrict) {
  const areas = (project.gardens || []).filter((g) => g.pts?.length > 2 && needsWater(g)).map((g) => ({ g, c: areaCoverage(g, project.fittings, strict) }));
  return { areas, plan: zonePlan(project) };
}
